// frontend/src/pages/admin/FreelancerSearch.jsx
// Search and Import Projects from Freelancer.com

import React, { useState } from 'react';
import api from '../../utils/api';

const FreelancerSearch = () => {
  const [searchKeywords, setSearchKeywords] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchKeywords.trim()) {
      alert('Please enter search keywords');
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/freelancer/search', {
        params: {
          keywords: searchKeywords,
          limit: 30
        }
      });
      
      setSearchResults(response.data.data);
    } catch (error) {
      console.error('Search error:', error);
      alert(error.response?.data?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (project) => {
    try {
      setImportingId(project.projectId);
      
      const response = await api.post('/freelancer/import', {
        projectId: project.projectId
      });
      
      alert('Project imported successfully!');
      
      // Remove from search results
      setSearchResults(prev => prev.filter(p => p.projectId !== project.projectId));
    } catch (error) {
      console.error('Import error:', error);
      alert(error.response?.data?.message || 'Import failed. Project may already exist.');
    } finally {
      setImportingId(null);
    }
  };

  const viewDetails = (project) => {
    setSelectedProject(project);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      awarded: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatBudget = (budget) => {
    if (!budget) return 'Not specified';
    
    const { min, max, currency } = budget;
    const curr = currency || 'USD';
    
    if (min && max) {
      return `${curr} $${min} - $${max}`;
    } else if (min) {
      return `${curr} $${min}+`;
    } else if (max) {
      return `${curr} Up to $${max}`;
    }
    
    return 'Not specified';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Search Freelancer Projects
        </h1>
        <p className="text-gray-600">
          Find and import projects from Freelancer.com
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by keywords (e.g., nodejs, react, python, logo design)..."
              value={searchKeywords}
              onChange={(e) => setSearchKeywords(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Searching...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </>
            )}
          </button>
        </form>

        {/* Quick Search Examples */}
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-sm text-gray-600">Popular:</span>
          {['nodejs', 'react', 'python', 'logo design', 'mobile app'].map((keyword) => (
            <button
              key={keyword}
              onClick={() => {
                setSearchKeywords(keyword);
                handleSearch({ preventDefault: () => {} });
              }}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition"
            >
              {keyword}
            </button>
          ))}
        </div>
      </div>

      {/* Search Results */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Searching Freelancer.com...</p>
        </div>
      ) : searchResults.length === 0 && searchKeywords ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 text-lg">No projects found</p>
          <p className="text-gray-400 text-sm mt-2">Try different keywords</p>
        </div>
      ) : searchResults.length > 0 ? (
        <>
          {/* Results Count */}
          <div className="mb-4">
            <p className="text-gray-600">
              Found <span className="font-semibold text-gray-800">{searchResults.length}</span> projects
            </p>
          </div>

          {/* Results Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {searchResults.map((project) => (
              <div
                key={project.projectId}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition p-6"
              >
                {/* Status & ID */}
                <div className="flex justify-between items-start mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                  <span className="text-xs text-gray-400">
                    ID: {project.projectId}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2 min-h-[56px]">
                  {project.projectTitle}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 mb-4 line-clamp-3 min-h-[60px]">
                  {project.projectDescription || 'No description available'}
                </p>

                {/* Stats */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Budget:</span>
                    <span className="font-semibold text-gray-800">
                      {formatBudget(project.budget)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Bids:</span>
                    <span className="font-semibold text-blue-600">
                      {project.totalBids || 0}
                    </span>
                  </div>
                  {project.averageBid && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Avg Bid:</span>
                      <span className="font-semibold text-green-600">
                        ${project.averageBid.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Posted:</span>
                    <span className="font-semibold text-gray-800">
                      {formatDate(project.postedDate)}
                    </span>
                  </div>
                </div>

                {/* Skills */}
                {project.skills && project.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.skills.slice(0, 4).map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
                      >
                        {skill}
                      </span>
                    ))}
                    {project.skills.length > 4 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        +{project.skills.length - 4} more
                      </span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => viewDetails(project)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => handleImport(project)}
                    disabled={importingId === project.projectId}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {importingId === project.projectId ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Importing...
                      </span>
                    ) : (
                      'Import'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-gray-500 text-lg">Start searching</p>
          <p className="text-gray-400 text-sm mt-2">Enter keywords to find projects on Freelancer.com</p>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1 pr-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {selectedProject.projectTitle}
                </h2>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedProject.status)}`}>
                    {selectedProject.status}
                  </span>
                  <span className="text-sm text-gray-500">
                    ID: {selectedProject.projectId}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Description</h3>
              <p className="text-gray-600 whitespace-pre-line">
                {selectedProject.projectDescription || 'No description available'}
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Bids</p>
                <p className="text-2xl font-bold text-blue-600">
                  {selectedProject.totalBids || 0}
                </p>
              </div>
              {selectedProject.averageBid && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Avg Bid</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${selectedProject.averageBid.toFixed(2)}
                  </p>
                </div>
              )}
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Budget</p>
                <p className="text-lg font-bold text-purple-600">
                  {formatBudget(selectedProject.budget)}
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Posted</p>
                <p className="text-sm font-bold text-yellow-600">
                  {formatDate(selectedProject.postedDate)}
                </p>
              </div>
            </div>

            {/* Skills */}
            {selectedProject.skills && selectedProject.skills.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Required Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-lg"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4 border-t">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleImport(selectedProject);
                  setShowDetailsModal(false);
                }}
                disabled={importingId === selectedProject.projectId}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-gray-400"
              >
                {importingId === selectedProject.projectId ? 'Importing...' : 'Import Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreelancerSearch;
